import HttpClient from '../../../core/HttpClient';
import Failure from '../../../core/Failure';
import Call from '../../models/Call';
import { Authentication } from '@leedpulse-monorepo/classes';

interface CallApiDoc {
    id?: string;
    created?: number;
    customer?: string;
    duration?: number;
    id_api_key?: string;
    id_contact?: string;
    platform?: string;
    price?: number;
    sender?: string;
    transcription?: string;
    transcription_srt?: string;
    transcription_raw?: string;
    direction?: string;
    missed?: boolean;
    readed_frontend?: boolean;
    hidden?: boolean;
}

interface SubscriptionRef {
    id: string;
    api_key: string;
}

/**
 * Count the number of calls for the current billing period for each subscription.
 *
 * Queries the Calls collection using `id_api_key` (= subscription.apiKey)
 * and `created >= billingStartDate`.
 *
 * @param subscriptions  – list of subscription refs (id + apiKey)
 * @param billingStartMs – start of the billing period as a timestamp in ms.
 * @param endTimestamp – end of the billing period as a timestamp in ms.
 * @returns A dictionary mapping subscription ID → call count for the period.
 */
export async function getCallsBySubscriptions(
    {
        subscriptions,
        startTimestamp,
        endTimestamp,
    }: {
        subscriptions: SubscriptionRef[],
        startTimestamp?: number,
        endTimestamp?: number,
    },
    companyApiKey: string,
    auth: Authentication,
): Promise<Record<string, Call[]>> {
    try {
        if (!subscriptions || subscriptions.length === 0 || !startTimestamp || !companyApiKey) return {};

        const subscriptionIds = subscriptions.map((s) => s.id);

        const response = await HttpClient.get(
            `/leedflow/stats/calls/by-subscriptions?subscription_ids=${encodeURIComponent(
                subscriptionIds.join(','),
            )}&start=${encodeURIComponent(String(startTimestamp))}${endTimestamp ? `&end=${encodeURIComponent(String(endTimestamp))}` : ''
            }`,
            auth.token,
            {
                'x-api-key': companyApiKey,
            },
        );

        const raw = (response?.data ?? {}) as Record<string, CallApiDoc[]>;

        const callsMap: Record<string, Call[]> = {};
        for (const [subscriptionId, calls] of Object.entries(raw)) {
            callsMap[subscriptionId] = (calls ?? []).map((c) => new Call(
                c.id ?? '',
                c.created ?? 0,
                c.customer ?? '',
                c.duration ?? 0,
                c.id_api_key ?? '',
                c.id_contact ?? '',
                c.platform ?? '',
                c.price ?? 0,
                c.sender ?? '',
                c.transcription ?? '',
                c.transcription_srt ?? '',
                c.transcription_raw ?? '',
                c.direction ?? '',
                c.missed ?? false,
                c.readed_frontend ?? false,
                c.hidden ?? false,
            ));
        }

        return callsMap;
    } catch (e) {
        console.error('getCallsCountBySubscriptions error:', e);
        throw new GetCallsCountBySubscriptionsFailure(
            (e as Error)?.message || 'Unknown error'
        );
    }
}

export class GetCallsCountBySubscriptionsFailure extends Failure {
    constructor(
        message = 'Unknown error',
        code = 'get-calls-count-by-subscriptions-failure',
    ) {
        super(message, code);
    }
}
